import api from './api';
import { Sale, SaleInstallment, Vehicle } from '../types';
import vehicleService from './vehicleService';

const saleService = {
  /**
   * Carrega parcelas das vendas a prazo parciais que ainda não trazem installments na listagem.
   */
  enrichTermSalesWithInstallments: async (sales: Sale[]): Promise<Sale[]> => {
    const needsFetch = sales.filter(
      (s) =>
        s.payment_method === 'term' &&
        s.payment_status === 'partial' &&
        !s.installments?.length
    )
    return saleService.enrichAllTermSalesWithInstallments(sales, needsFetch)
  },

  /** Carrega parcelas de todas as vendas a prazo que ainda não trazem installments (relatórios / PDF). */
  enrichAllTermSalesWithInstallments: async (
    sales: Sale[],
    onlySales?: Sale[]
  ): Promise<Sale[]> => {
    const pool = onlySales ?? sales
    const needsFetch = pool.filter(
      (s) => s.payment_method === 'term' && !s.installments?.length
    )
    if (needsFetch.length === 0) return sales

    const fetched = await Promise.all(
      needsFetch.map(async (sale) => {
        try {
          const installments = await saleService.getInstallments(sale.id)
          return { id: sale.id, installments }
        } catch {
          return { id: sale.id, installments: [] as Sale['installments'] }
        }
      })
    )

    const byId = Object.fromEntries(fetched.map((item) => [item.id, item.installments]))
    return sales.map((sale) =>
      byId[sale.id] ? { ...sale, installments: byId[sale.id] } : sale
    )
  },

  /**
   * Busca todas as vendas
   */
  getAllSales: async (): Promise<Sale[]> => {
    try {
      const response = await api.get('/sales');
      return response.data || [];
    } catch (error) {
      console.error('Erro ao buscar vendas:', error);
      throw error;
    }
  },

  /**
   * Busca uma venda específica
   * @param id ID da venda
   */
  getSale: async (id: number): Promise<Sale> => {
    try {
      const response = await api.get(`/sales/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar venda ${id}:`, error);
      throw error;
    }
  },

  /**
   * Cria uma nova venda
   * @param sale Dados da venda
   */
  createSale: async (sale: any): Promise<Sale> => {
    try {
      let payload = sale

      if (!(sale instanceof FormData)) {
        payload = new FormData()

        const formattedSale = {
          vehicle_id: sale.vehicle_id,
          sale_price: typeof sale.sale_price === 'string'
            ? parseFloat(String(sale.sale_price || '0').replace(/[^\d.-]/g, ''))
            : sale.sale_price,
          sale_date: sale.sale_date,
          customer_name: sale.customer_name?.trim(),
          buyer_phone: sale.customer_contact?.trim(),
          payment_method: sale.payment_method,
          buyer_name: sale.customer_name?.trim(),
          buyer_document: sale.buyer_document?.trim(),
          payment_status: (sale.payment_status || 'pending').toLowerCase().trim(),
          notes: sale.notes
        }

        if (formattedSale.sale_date) {
          const date = new Date(formattedSale.sale_date)
          if (!isNaN(date.getTime())) {
            formattedSale.sale_date = date.toISOString().split('T')[0]
          }
        }

        const validPaymentStatus = ['pending', 'paid', 'partial']
        if (!validPaymentStatus.includes(formattedSale.payment_status)) {
          formattedSale.payment_status = 'pending'
        }

        Object.entries(formattedSale).forEach(([k, v]) => {
          if (v !== undefined && v !== null) {
            payload.append(k, String(v))
          }
        })
      }

      const response = await api.post('/sales', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      return response.data
    } catch (error) {
      console.error('Erro ao criar venda:', error)
      throw error
    }
  },

  /**
   * Atualiza uma venda existente
   * @param id ID da venda
   * @param sale Dados atualizados da venda
   */
  updateSale: async (id: number, sale: any): Promise<Sale> => {
    try {
      let payload = sale

      if (!(sale instanceof FormData)) {
        payload = new FormData()

        const formattedSale = {
          vehicle_id: sale.vehicle_id,
          sale_price: typeof sale.sale_price === 'string'
            ? parseFloat(String(sale.sale_price || '0').replace(/[^\d.-]/g, ''))
            : sale.sale_price,
          sale_date: sale.sale_date,
          buyer_phone: sale.buyer_phone?.trim(),
          payment_method: sale.payment_method,
          buyer_name: sale.buyer_name?.trim(),
          buyer_document: sale.buyer_document?.trim(),
          payment_status: (sale.payment_status || 'pending').toLowerCase().trim(),
          notes: sale.notes
        }

        if (formattedSale.sale_date) {
          const date = new Date(formattedSale.sale_date)
          if (!isNaN(date.getTime())) {
            formattedSale.sale_date = date.toISOString().split('T')[0]
          }
        }

        const validPaymentStatus = ['pending', 'paid', 'partial']
        if (!validPaymentStatus.includes(formattedSale.payment_status)) {
          formattedSale.payment_status = 'pending'
        }

        Object.entries(formattedSale).forEach(([k, v]) => {
          if (v !== undefined && v !== null) {
            payload.append(k, String(v))
          }
        })

        if (sale.documents_add?.length) {
          sale.documents_add.forEach((doc: any, i: number) => {
            payload.append(`documents_add[${i}][file]`, doc.file)
            payload.append(`documents_add[${i}][type]`, doc.type)
          })
        }

        if (sale.documents_delete?.length) {
          sale.documents_delete.forEach((id: number, i: number) => {
            payload.append(`documents_delete[${i}]`, String(id))
          })
        }
      }

      const response = await api.post(`/sales/${id}?_method=PUT`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      return response.data
    } catch (error) {
      console.error(`Erro ao atualizar venda ${id}:`, error)
      throw error
    }
  },
  /**
   * Exclui uma venda
   * @param id ID da venda
   */
  deleteSale: async (id: number, vehicleId?: number): Promise<void> => {
    try {
      let vehicleIdToRestore = vehicleId;

      if (!vehicleIdToRestore) {
        try {
          const sale = await saleService.getSale(id);
          vehicleIdToRestore = sale.vehicle_id;
        } catch (fetchError) {
          console.warn(`Não foi possível obter veículo da venda ${id} antes da exclusão:`, fetchError);
        }
      }

      await api.delete(`/sales/${id}`);

      if (vehicleIdToRestore) {
        try {
          await vehicleService.updateVehicleStatus(vehicleIdToRestore, 'available');
        } catch (vehicleError) {
          console.error(`Erro ao restaurar status do veículo ${vehicleIdToRestore} para 'available':`, vehicleError);
        }
      }
    } catch (error) {
      console.error(`Erro ao excluir venda ${id}:`, error);
      throw error;
    }
  },

  /**
   * Busca um resumo das vendas (total, média, etc.)
   */
  getSummary: async () => {
    try {

      const response = await api.get('/summary');
      return response.data;

    } catch (error) {
      console.error('Erro ao buscar resumo das vendas:', error);
      throw error;
      
    }
  },
  /**
 * Busca os veiculos disponiveis e em manutentção, para o layout
 */
  getSummaryFrota: async () => {
    try {
      const response = await api.get('/frota-summary');
      return response.data;
    } catch (error: any) {
      // Se for 404, o endpoint não existe no backend
      if (error?.response?.status === 404) {
        console.warn('Endpoint /frota-summary não encontrado no backend. Verifique se a rota está implementada.');
      } else {
        console.error('Erro ao buscar resumo da frota:', error);
      }
      throw error;
    }
  },

  /**
   * Busca o lucro de um veículo específico
   * @param vehicleId ID do veículo
   */

  /**
   * Obtém vendas por mês
   */
  getSalesByMonth: async () => {
    try {
      const response = await api.get('/sales/by-month');
      return response.data || [];
    } catch (error) {
      console.error('Erro ao buscar vendas por mês:', error);
      throw error;
    }
  },

  /**
   * Obtém estatísticas de vendas
   */
  getSaleStats: async () => {
    try {
      const response = await api.get('/sales/stats');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estatísticas de vendas:', error);
      throw error;
    }
  },

  /**
   * Busca veículos disponíveis para venda
   */
  getVehiclesForSale: async (): Promise<Vehicle[]> => {
    try {
      return await vehicleService.getAvailableVehicles();
    } catch (error) {
      console.error('Erro ao buscar veículos disponíveis para venda:', error);
      throw error;
    }
  },

  /**
   * Parcelas da venda a prazo (check de pagamento).
   * GET /sales/:saleId/installments — pode vir vazio até o backend gerar parcelas ao salvar a venda.
   */
  getInstallments: async (saleId: number): Promise<SaleInstallment[]> => {
    try {
      const response = await api.get(`/sales/${saleId}/installments`);
      const raw = response.data;
      return Array.isArray(raw) ? raw : raw?.data ?? [];
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) return [];
      console.error(`Erro ao buscar parcelas da venda ${saleId}:`, error);
      throw error;
    }
  },

  /**
   * Registra pagamento de uma parcela.
   * POST /sales/:saleId/installments/:installmentId/pay — body opcional { paid_at: 'YYYY-MM-DD' }
   */
  payInstallment: async (
    saleId: number,
    installmentId: number,
    payload?: { paid_at?: string }
  ): Promise<SaleInstallment> => {
    const paidAt = payload?.paid_at ?? new Date().toISOString().slice(0, 10);
    const response = await api.post(`/sales/${saleId}/installments/${installmentId}/pay`, {
      paid_at: paidAt,
    });
    return response.data;
  },

  /**
   * Desfaz baixa da parcela (volta para pendente).
   * POST /sales/:saleId/installments/:installmentId/unpay
   */
  unpayInstallment: async (saleId: number, installmentId: number): Promise<SaleInstallment> => {
    const response = await api.post(`/sales/${saleId}/installments/${installmentId}/unpay`);
    return response.data;
  },
};

export default saleService; 